// pages/NuevaFactura.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { crearFactura } from '../services/facturaService';
import api from '../services/api';
import Swal from 'sweetalert2';
import '../styles/NuevaFactura.css';
import { obtenerFechaLocal } from '../utils/dateUtils';
import { useFiscal } from '../hooks/useFiscal';

// Tasa local de ITBIS por IndicadorFacturacion DGII (solo preview; el servidor recalcula)
const ITBIS_TASA_LOCAL = { 1: 0.18, 2: 0.16, 3: 0.00, 4: 0.00 };
const ITBIS_OPCIONES = [
  { value: 1, label: 'Gravado 18%' },
  { value: 2, label: 'Gravado 16%' },
  { value: 3, label: 'Tasa cero 0%' },
  { value: 4, label: 'Exento' },
];
const ITBIS_LABEL_SUFIJO = { 1: '18%', 2: '16%', 3: '0%', 4: 'Exento' };

const NuevaFactura = () => {
  const navigate = useNavigate();
  const fiscal = useFiscal();
  const [loading, setLoading] = useState(false);
  const [proyectos, setProyectos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [materiales, setMateriales] = useState([]);
  const [partidas, setPartidas] = useState([]);
  const [modalCliente, setModalCliente] = useState(false);
  const [nuevoCliente, setNuevoCliente] = useState({ nombre: '', rnc_cedula: '', telefono: '', email: '' });
  const [guardandoCliente, setGuardandoCliente] = useState(false);
  
  const [formData, setFormData] = useState({
    ProyectoId: '',
    ClienteId: '',
 fecha_emision: obtenerFechaLocal(),
    fecha_vencimiento: '',
    tipo_ncf: '',
    ncf: '',
    concepto: '',
    observaciones: '',
    retencion: 0
  });

  const [items, setItems] = useState([]);

  useEffect(() => {
    cargarDatos();
    const fechaVenc = calcularFechaVencimiento();
    setFormData(prev => ({ ...prev, fecha_vencimiento: fechaVenc }));
  }, []);

  const cargarDatos = async () => {
  try {
    const [resProyectos, resClientes, resMateriales] = await Promise.all([
      api.get('/proyectos'),
      api.get('/clientes?activos=true'),
      api.get('/materiales'),
    ]);

    const proyectosData = Array.isArray(resProyectos.data)
      ? resProyectos.data
      : resProyectos.data.proyectos || [];

    const clientesData = Array.isArray(resClientes.data)
      ? resClientes.data
      : resClientes.data.clientes || [];

    setProyectos(proyectosData);
    setClientes(clientesData);
    setMateriales(Array.isArray(resMateriales.data) ? resMateriales.data : []);

    // Cargar partidas por separado — no rompe si falla
    try {
      const resPartidas = await api.get('/partidas-estandar');
      const partidasData = Array.isArray(resPartidas.data) ? resPartidas.data : resPartidas.data?.partidas || [];
      setPartidas(partidasData);
    } catch {
      setPartidas([]);
    }
  } catch (error) {
    console.error('Error cargando datos:', error);
    Swal.fire({ icon: 'error', title: 'Error', text: 'Error al cargar datos' });
  }
};

  const calcularFechaVencimiento = () => {
  const hoy = new Date();
  hoy.setDate(hoy.getDate() + 30);
  const year = hoy.getFullYear();
  const month = String(hoy.getMonth() + 1).padStart(2, '0');
  const day = String(hoy.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
const handleChange = (e) => {
  const { name, value, type } = e.target;
  
  // Si es un campo numérico y está vacío, usar 0
  const finalValue = type === 'number' && value === '' ? 0 : value;
  
  setFormData(prev => ({ ...prev, [name]: finalValue }));

  };

  const eliminarItem = (index) => {
    // Fix: quitar el guard `items.length > 1` que impedía borrar la última línea.
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const r2 = (n) => Math.round(n * 100) / 100;

  const calcularSubtotalItem = (item) => {
    return r2(parseFloat(item.cantidad || 0) * parseFloat(item.precio_unitario || 0));
  };

  const calcularSubtotal = () => {
    return r2(items.reduce((sum, item) => sum + calcularSubtotalItem(item), 0));
  };

  // ITBIS por línea con la tasa local del indicador (preview; el server recalcula server-side)
  const calcularItbisItem = (item) => {
    const tasa = ITBIS_TASA_LOCAL[item.itbis_indicador ?? 1] ?? 0.18;
    return r2(calcularSubtotalItem(item) * tasa);
  };

  const calcularITBIS = () => {
    return r2(items.reduce((sum, item) => sum + calcularItbisItem(item), 0));
  };

  // Etiqueta dinámica del ITBIS en el resumen según los indicadores de las líneas
  const etiquetaItbis = () => {
    const base = fiscal.impuesto_label || 'ITBIS';
    if (items.length === 0) return base;
    const indicadores = [...new Set(items.map(i => Number(i.itbis_indicador ?? 1)))];
    if (indicadores.length > 1) return base;                          // tasas mixtas → solo "ITBIS"
    return `${base} (${ITBIS_LABEL_SUFIJO[indicadores[0]] ?? '18%'})`; // todas iguales → "(18%)" / "(Exento)"...
  };

  const calcularTotal = () => {
    return r2(calcularSubtotal() + calcularITBIS() - parseFloat(formData.retencion || 0));
  };



  // Crear cliente rápido sin salir de la factura
  const crearClienteRapido = async () => {
    if (!nuevoCliente.nombre.trim()) { Swal.fire({ icon: 'warning', title: 'Validación', text: 'El nombre es obligatorio' }); return; }
    setGuardandoCliente(true);
    try {
      const res = await api.post('/clientes', nuevoCliente);
      const clienteCreado = res.data?.cliente || res.data;
      if (!clienteCreado?.id) throw new Error('No se recibió el cliente creado');
      setClientes(prev => [...prev, clienteCreado]);
      setFormData(prev => ({ ...prev, ClienteId: String(clienteCreado.id) }));
      setModalCliente(false);
      setNuevoCliente({ nombre: '', rnc_cedula: '', telefono: '', email: '' });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.response?.data?.error || 'Error al crear cliente' });
    } finally {
      setGuardandoCliente(false);
    }
  };

  const formatearMonto = (monto) => {
    try {
      return new Intl.NumberFormat('es-DO', {
        style: 'currency',
        currency: fiscal.moneda?.codigo || 'DOP'
      }).format(monto);
    } catch {
      return `${fiscal.moneda?.simbolo || 'RD$'} ${Number(monto).toFixed(2)}`;
    }
  };

  const validarFormulario = async () => {
    if (!formData.ClienteId) {
      await Swal.fire({ icon: 'warning', title: 'Validación', text: 'Selecciona un cliente' });
      return false;
    }
    if (items.length === 0) {
      await Swal.fire({ icon: 'warning', title: 'Validación', text: 'Agrega al menos un item' });
      return false;
    }
    for (let i = 0; i < items.length; i++) {
      if (!items[i].descripcion) {
        await Swal.fire({ icon: 'warning', title: 'Validación', text: `El item ${i + 1} necesita una descripción` });
        return false;
      }
      if (items[i].cantidad <= 0) {
        await Swal.fire({ icon: 'warning', title: 'Validación', text: `El item ${i + 1} necesita una cantidad válida` });
        return false;
      }
      if (items[i].precio_unitario <= 0) {
        await Swal.fire({ icon: 'warning', title: 'Validación', text: `El item ${i + 1} necesita un precio válido` });
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!await validarFormulario()) return;

    try {
      setLoading(true);
      
      const datos = {
        ...formData,
        items: items.map(item => ({
          descripcion: item.descripcion,
          cantidad: parseFloat(item.cantidad),
          precio_unitario: parseFloat(item.precio_unitario),
          itbis_indicador: Number(item.itbis_indicador) || 1,   // ← NUEVO (solo indicador; tasa/linea las calcula el server)
          partida: item.partida || null,
          MaterialId: item.MaterialId || null,
          PartidaId: item.PartidaId || null,
        }))
      };

      await crearFactura(datos);
      await Swal.fire({ icon: 'success', title: '¡Listo!', text: 'Factura creada exitosamente' });
      navigate('/facturas');
    } catch (error) {
      console.error('Error creando factura:', error);
      Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.error || 'Error al crear factura' });
    } finally {
      setLoading(false);
    }
  };

 const generarNCF = async () => {
  if (!formData.tipo_ncf) {
    Swal.fire({ icon: 'warning', title: 'Validación', text: 'Primero selecciona un tipo de NCF' });
    return;
  }

  try {
    // Extraer solo el código numérico (B01 -> 01)
    const codigoNCF = formData.tipo_ncf.replace(/[^0-9]/g, '');
    
    const response = await api.post('/ncf/generar', {
      tipo_ncf: codigoNCF
    });
    setFormData({
      ...formData,
      ncf: response.data.ncf
    });

    // Mostrar info de disponibles
    if (response.data.disponibles < 10) {
      Swal.fire({ icon: 'warning', title: 'Atención', text: `Solo quedan ${response.data.disponibles} NCF disponibles del tipo ${formData.tipo_ncf}` });
    }
  } catch (error) {
    console.error('Error generando NCF:', error);
    Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.error || 'Error al generar NCF. Verifica que tengas secuencias activas.' });
  }
};

  return (
    <div className="nueva-factura-container">
      {/* Header */}
      <div className="nueva-factura-header">
        <div>
          <h1>Nueva Factura</h1>
          <p className="subtitle">Crear factura</p>
        </div>
        <button 
          className="btn btn-secondary"
          onClick={() => navigate('/facturas')}
        >
          ← Volver
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Datos Generales */}
        <div className="card">
          <h2 className="card-title">Datos Generales</h2>
          
          <div className="form-grid">
            <div className="form-group">
              <label>Proyecto <span style={{color:'#6c757d',fontWeight:'400'}}>(opcional)</span></label>
              <select
                name="ProyectoId"
                value={formData.ProyectoId}
                onChange={handleChange}
                className="form-control"
              >
                <option value="">Sin proyecto</option>
                {proyectos.map(p => (
                  <option key={p.id} value={p.id}>{p.nombre}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Cliente *</label>
              <div style={{display:'flex', gap:'8px'}}>
                <select
                  name="ClienteId"
                  value={formData.ClienteId}
                  onChange={handleChange}
                  required
                  className="form-control"
                >
                  <option value="">Selecciona un cliente</option>
                  {clientes.map(c => (
                    <option key={c.id} value={c.id}>{c.nombre}{c.rnc_cedula ? ` — ${c.rnc_cedula}` : ''}</option>
                  ))}
                </select>
                <button type="button" className="btn btn-success" style={{whiteSpace:'nowrap'}}
                  onClick={() => setModalCliente(true)} title="Crear nuevo cliente">
                  ➕
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Fecha Emisión *</label>
              <input
                type="date"
                name="fecha_emision"
                value={formData.fecha_emision}
                onChange={handleChange}
                required
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label>Fecha Vencimiento *</label>
              <input
                type="date"
                name="fecha_vencimiento"
                value={formData.fecha_vencimiento}
                onChange={handleChange}
                required
                className="form-control"
              />
            </div>

            {fiscal.tiene_comprobantes !== false && (
            <div className="form-group">
              <label>Tipo {fiscal.sistema_comprobantes || 'NCF'}</label>
              <select
                name="tipo_ncf"
                value={formData.tipo_ncf}
                onChange={handleChange}
                className="form-control"
              >
                <option value="">Sin {fiscal.sistema_comprobantes || 'NCF'}</option>
                {fiscal.tipos_comprobantes && (() => {
                  const tiposB = Object.entries(fiscal.tipos_comprobantes).filter(([c]) => c.startsWith('B'));
                  const tiposE = Object.entries(fiscal.tipos_comprobantes).filter(([c]) => c.startsWith('E'));
                  return (
                    <>
                      {tiposB.length > 0 && (
                        <optgroup label="── Serie B — Tradicionales ──">
                          {tiposB.map(([codigo, info]) => (
                            <option key={codigo} value={codigo}>{codigo} - {info.label}</option>
                          ))}
                        </optgroup>
                      )}
                      {tiposE.length > 0 && (
                        <optgroup label="── Serie E — Electrónicos (e-CF) ──">
                          {tiposE.map(([codigo, info]) => (
                            <option key={codigo} value={codigo}>{codigo} - {info.label}</option>
                          ))}
                        </optgroup>
                      )}
                    </>
                  );
                })()}
              </select>
            </div>
            )}

            {fiscal.tiene_comprobantes !== false && (
           <div className="form-group">
  <label>{fiscal.sistema_comprobantes || 'NCF'}</label>
  <div style={{ display: 'flex', gap: '0.5rem' }}>
    <input
      type="text"
      name="ncf"
      value={formData.ncf}
      onChange={handleChange}
      placeholder="Se generará automáticamente"
      className="form-control"
      readOnly
      style={{ background: '#f8f9fa' }}
    />
    <button
      type="button"
      onClick={generarNCF}
      className="btn btn-primary"
      style={{ whiteSpace: 'nowrap' }}
      disabled={!formData.tipo_ncf}
    >
      🔄 Generar {fiscal.sistema_comprobantes || 'NCF'}
    </button>
  </div>
  {formData.tipo_ncf && !formData.ncf && (
    <small style={{ color: '#6c757d', marginTop: '0.5rem', display: 'block' }}>
      Click en "Generar {fiscal.sistema_comprobantes || 'NCF'}" para asignar automáticamente
    </small>
  )}
</div>
            )}

            <div className="form-group full-width">
              <label>Concepto</label>
              <input
                type="text"
                name="concepto"
                value={formData.concepto}
                onChange={handleChange}
                placeholder="Descripción general de la factura"
                className="form-control"
              />
            </div>

            <div className="form-group full-width">
              <label>Observaciones</label>
              <textarea
                name="observaciones"
                value={formData.observaciones}
                onChange={handleChange}
                rows="3"
                className="form-control"
              />
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="card">
          <h2 className="card-title">Items de Factura</h2>

          {/* PANEL SELECTOR — arriba, siempre visible */}
          <div style={{background:'#f8fafc', borderRadius:'10px', padding:'16px', marginBottom:'20px', border:'1px solid #e2e8f0'}}>
            <p style={{margin:'0 0 12px', fontWeight:'700', color:'#334155', fontSize:'14px'}}>➕ Agregar línea</p>
            <div style={{display:'flex', gap:'10px', flexWrap:'wrap'}}>

              {/* SELECTOR SERVICIO */}
              <div style={{flex:'1', minWidth:'220px'}}>
                <label style={{fontSize:'12px', fontWeight:'600', color:'#0066cc', display:'block', marginBottom:'4px'}}>🔧 Servicio / Partida</label>
                <select className="form-control"
                  value=""
                  onChange={(e) => {
                    if (!e.target.value) return;
                    const p = partidas.find(p => p.id === parseInt(e.target.value));
                    if (p) {
                      setItems(prev => [...prev, {
                        tipo: 'servicio', PartidaId: p.id, MaterialId: null,
                        descripcion: p.nombre, partida: p.codigo || '',
                        cantidad: 1, precio_unitario: parseFloat(p.precio_referencia || 0),
                        itbis_indicador: 1,   // ← NUEVO (default 18%)
                      }]);
                    }
                    e.target.value = '';
                  }}
                >
                  <option value="">-- Selecciona servicio --</option>
                  {partidas.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.codigo ? `[${p.codigo}] ` : ''}{p.nombre}
                    </option>
                  ))}
                </select>
              </div>

              {/* SELECTOR PRODUCTO — solo sin proyecto */}
              {!formData.ProyectoId && <div style={{flex:'1', minWidth:'220px'}}>
                <label style={{fontSize:'12px', fontWeight:'600', color:'#28a745', display:'block', marginBottom:'4px'}}>📦 Producto Inventario</label>
                <select className="form-control"
                  value=""
                  onChange={(e) => {
                    if (!e.target.value) return;
                    const m = materiales.find(m => m.id === parseInt(e.target.value));
                    if (m) {
                      setItems(prev => [...prev, {
                        tipo: 'producto', MaterialId: m.id, PartidaId: null,
                        descripcion: m.nombre, partida: m.codigo || '',
                        cantidad: 1, precio_unitario: parseFloat(m.precio_venta || m.costo_unitario_inicial || 0),
                        itbis_indicador: 1,   // ← NUEVO (default 18%)
                      }]);
                    }
                    e.target.value = '';
                  }}
                >
                  <option value="">-- Selecciona producto --</option>
                  {materiales.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.codigo} - {m.nombre}{m.precio_venta > 0 ? ` | RD$ ${parseFloat(m.precio_venta).toFixed(2)}` : ''}
                    </option>
                  ))}
                </select>
              </div>}

              {/* LÍNEA LIBRE */}
              <div style={{display:'flex', alignItems:'flex-end'}}>
                <button type="button" className="btn btn-secondary"
                  onClick={() => setItems(prev => [...prev, { tipo:'libre', MaterialId:null, PartidaId:null, descripcion:'', partida:'', cantidad:1, precio_unitario:0, itbis_indicador:1 }])}>
                  ✏️ Línea Libre
                </button>
              </div>
            </div>
          </div>

          {/* TABLA DE LÍNEAS */}
          {items.length === 0 ? (
            <div style={{textAlign:'center', padding:'30px', color:'#94a3b8', border:'2px dashed #e2e8f0', borderRadius:'8px'}}>
              Agrega servicios, productos o líneas libres desde el panel de arriba
            </div>
          ) : (
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%', borderCollapse:'collapse'}}>
                <thead>
                  <tr style={{background:'#f8fafc'}}>
                    <th style={{padding:'8px 10px', textAlign:'left', fontSize:'12px', color:'#64748b', fontWeight:'700', borderBottom:'2px solid #e2e8f0'}}>#</th>
                    <th style={{padding:'8px 10px', textAlign:'left', fontSize:'12px', color:'#64748b', fontWeight:'700', borderBottom:'2px solid #e2e8f0'}}>Tipo</th>
                    <th style={{padding:'8px 10px', textAlign:'left', fontSize:'12px', color:'#64748b', fontWeight:'700', borderBottom:'2px solid #e2e8f0'}}>Descripción</th>
                    <th style={{padding:'8px 10px', textAlign:'left', fontSize:'12px', color:'#64748b', fontWeight:'700', borderBottom:'2px solid #e2e8f0'}}>Ref.</th>
                    <th style={{padding:'8px 10px', textAlign:'right', fontSize:'12px', color:'#64748b', fontWeight:'700', borderBottom:'2px solid #e2e8f0'}}>Cant.</th>
                    <th style={{padding:'8px 10px', textAlign:'right', fontSize:'12px', color:'#64748b', fontWeight:'700', borderBottom:'2px solid #e2e8f0'}}>Precio Unit.</th>
                    <th style={{padding:'8px 10px', textAlign:'center', fontSize:'12px', color:'#64748b', fontWeight:'700', borderBottom:'2px solid #e2e8f0'}}>ITBIS</th>
                    <th style={{padding:'8px 10px', textAlign:'right', fontSize:'12px', color:'#64748b', fontWeight:'700', borderBottom:'2px solid #e2e8f0'}}>Subtotal</th>
                    <th style={{padding:'8px 10px', borderBottom:'2px solid #e2e8f0'}}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index} style={{borderBottom:'1px solid #f1f5f9'}}>
                      <td style={{padding:'8px 10px', color:'#94a3b8', fontSize:'13px'}}>{index + 1}</td>
                      <td style={{padding:'8px 10px'}}>
                        <span style={{
                          padding:'2px 8px', borderRadius:'10px', fontSize:'11px', fontWeight:'700',
                          background: item.tipo==='servicio' ? '#e8f0fe' : item.tipo==='producto' ? '#e8f5e9' : '#fef3c7',
                          color:      item.tipo==='servicio' ? '#0066cc' : item.tipo==='producto' ? '#28a745' : '#f59e0b'
                        }}>
                          {item.tipo==='servicio' ? '🔧' : item.tipo==='producto' ? '📦' : '✏️'} {item.tipo}
                        </span>
                      </td>
                      <td style={{padding:'8px 10px'}}>
                        <input type="text" className="form-control" value={item.descripcion}
                          onChange={(e) => handleItemChange(index, 'descripcion', e.target.value)}
                          style={{minWidth:'180px'}} required />
                      </td>
                      <td style={{padding:'8px 10px'}}>
                        <input type="text" className="form-control" value={item.partida || ''}
                          onChange={(e) => handleItemChange(index, 'partida', e.target.value)}
                          style={{width:'90px'}} placeholder="Ref." />
                      </td>
                      <td style={{padding:'8px 10px'}}>
                        <input type="number" className="form-control" value={item.cantidad}
                          onChange={(e) => handleItemChange(index, 'cantidad', e.target.value)}
                          min="0.01" step="0.01" style={{width:'70px', textAlign:'right'}} required />
                      </td>
                      <td style={{padding:'8px 10px'}}>
                        <input type="number" className="form-control" value={item.precio_unitario}
                          onChange={(e) => handleItemChange(index, 'precio_unitario', e.target.value)}
                          min="0" step="0.01" style={{width:'100px', textAlign:'right'}} required />
                      </td>
                      <td style={{padding:'8px 10px', textAlign:'center'}}>
                        <select className="form-control"
                          value={item.itbis_indicador ?? 1}
                          onChange={(e) => handleItemChange(index, 'itbis_indicador', Number(e.target.value))}
                          style={{width:'130px'}}>
                          {ITBIS_OPCIONES.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </td>
                      <td style={{padding:'8px 10px', textAlign:'right', fontWeight:'700', color:'#0066cc'}}>
                        {formatearMonto(calcularSubtotalItem(item))}
                      </td>
                      <td style={{padding:'8px 10px'}}>
                        <button type="button"
                          onClick={() => eliminarItem(index)}
                          style={{background:'none', border:'none', cursor:'pointer', fontSize:'16px', color:'#dc3545'}}
                          title="Eliminar">🗑️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Resumen */}
        <div className="card">
          <h2 className="card-title">Resumen</h2>
          
          <div className="resumen-container">
            <div className="resumen-row">
              <span className="resumen-label">Subtotal:</span>
              <span className="resumen-value">{formatearMonto(calcularSubtotal())}</span>
            </div>
            
            <div className="resumen-row">
              <span className="resumen-label">{etiquetaItbis()}:</span>
              <span className="resumen-value">{formatearMonto(calcularITBIS())}</span>
            </div>
            
            <div className="resumen-row">
              <div className="resumen-label">
                Retención:
                <input
                  type="number"
                  name="retencion"
                  value={formData.retencion}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className="form-control-inline"
                />
              </div>
              <span className="resumen-value">{formatearMonto(formData.retencion)}</span>
            </div>
            
            <div className="resumen-row resumen-total">
              <span className="resumen-label">TOTAL:</span>
              <span className="resumen-value">{formatearMonto(calcularTotal())}</span>
            </div>
          </div>
        </div>

        {/* Botones */}
        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/facturas')}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Guardando...' : '💾 Guardar Factura'}
          </button>
        </div>
      </form>

      {/* MODAL CLIENTE RÁPIDO */}
      {modalCliente && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div style={{background:'white',borderRadius:'12px',padding:'24px',width:'100%',maxWidth:'460px',boxShadow:'0 10px 40px rgba(0,0,0,.3)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'20px'}}>
              <h2 style={{margin:0,fontSize:'18px',fontWeight:'700'}}>➕ Nuevo Cliente</h2>
              <button onClick={() => setModalCliente(false)} style={{background:'none',border:'none',fontSize:'20px',cursor:'pointer',color:'#6c757d'}}>✕</button>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
              <div className="form-group">
                <label>Nombre *</label>
                <input className="form-control" placeholder="Nombre del cliente"
                  value={nuevoCliente.nombre} onChange={e => setNuevoCliente({...nuevoCliente, nombre: e.target.value})} />
              </div>
              <div className="form-group">
                <label>RNC / Cédula</label>
                <input className="form-control" placeholder="000-0000000-0"
                  value={nuevoCliente.rnc_cedula} onChange={e => setNuevoCliente({...nuevoCliente, rnc_cedula: e.target.value})} />
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                <div className="form-group">
                  <label>Teléfono</label>
                  <input className="form-control" placeholder="809-000-0000"
                    value={nuevoCliente.telefono} onChange={e => setNuevoCliente({...nuevoCliente, telefono: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input className="form-control" placeholder="correo@empresa.com"
                    value={nuevoCliente.email} onChange={e => setNuevoCliente({...nuevoCliente, email: e.target.value})} />
                </div>
              </div>
            </div>
            <div style={{display:'flex',justifyContent:'flex-end',gap:'10px',marginTop:'20px'}}>
              <button className="btn btn-secondary" onClick={() => setModalCliente(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={crearClienteRapido} disabled={guardandoCliente}>
                {guardandoCliente ? 'Guardando...' : '💾 Guardar Cliente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NuevaFactura;